const CART_COUPON_STORAGE_KEY = 'cart_applied_coupon_code';
const CART_COUPON_CHECKOUT_ONLY_KEY = 'cart_coupon_checkout_only';
const MR_DRAWER_CROSS_SELL_ID = 'mr-div-embedded-cp-any-drawer';
const CART_DRAWER_COUPON_SLOT_SELECTOR =
  '#cart-drawer-coupon-slot, .cart-drawer__coupon-slot';

function ensureCartDrawerCouponAboveMrWidget() {
  const inner = document.querySelector('cart-drawer .drawer__inner');
  if (!inner) return;

  const slot = inner.querySelector(CART_DRAWER_COUPON_SLOT_SELECTOR);
  const mr = inner.querySelector(`#${MR_DRAWER_CROSS_SELL_ID}`);
  if (!slot || !mr || slot === mr) return;

  if (slot.nextElementSibling !== mr) {
    mr.parentNode.insertBefore(slot, mr);
  }
}

function initCartDrawerCouponPositionObserver() {
  if (window.__geekoCartDrawerCouponMrObserver) return;
  window.__geekoCartDrawerCouponMrObserver = true;

  const start = () => {
    const inner = document.querySelector('cart-drawer .drawer__inner');
    if (!inner) return;

    ensureCartDrawerCouponAboveMrWidget();

    const obs = new MutationObserver(() => {
      ensureCartDrawerCouponAboveMrWidget();
    });
    obs.observe(inner, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

function getCartUpdateJsUrl() {
  if (typeof routes !== 'undefined' && routes.cart_update_js_url) {
    return routes.cart_update_js_url;
  }
  const base =
    (typeof routes !== 'undefined' && routes.cart_update_url) || '/cart/update';
  return base.endsWith('.js') ? base : `${base}.js`;
}

class CartCoupon extends HTMLElement {
  constructor() {
    super();
    this._onApply = this._onApply.bind(this);
    this._onRemove = this._onRemove.bind(this);
  }

  connectedCallback() {
    this._cacheElements();
    this._syncFromUrl();
    this._restoreCheckoutOnlyState();
    this._syncAppliedUiFromDom();
    this._bindEvents();
    if (this.closest('cart-drawer')) {
      ensureCartDrawerCouponAboveMrWidget();
    }
  }

  disconnectedCallback() {
    this._unbindEvents();
  }

  _cacheElements() {
    this._input = this.querySelector('.cart-coupon__input');
    this._tag = this.querySelector('.cart-coupon__tag');
    this._tagText = this.querySelector('.cart-coupon__tag-text');
    this._applyBtn = this.querySelector('.cart-coupon__btn--apply');
    this._removeBtn = this.querySelector('.cart-coupon__btn--remove');
    this._tagRemoveBtn = this.querySelector('.cart-coupon__tag-remove');
    this._errorEl = this.querySelector('.cart-coupon__error');
  }

  _bindEvents() {
    this._applyBtn?.addEventListener('click', this._onApply);
    this._removeBtn?.addEventListener('click', this._onRemove);
    this._tagRemoveBtn?.addEventListener('click', this._onRemove);
    this._input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!this.classList.contains('cart-coupon--applied')) this._onApply();
      }
    });
  }

  _unbindEvents() {
    this._applyBtn?.removeEventListener('click', this._onApply);
    this._removeBtn?.removeEventListener('click', this._onRemove);
    this._tagRemoveBtn?.removeEventListener('click', this._onRemove);
  }

  _syncFromUrl() {
    const urlCode = new URLSearchParams(window.location.search).get('discount');
    if (urlCode) {
      try {
        sessionStorage.setItem(CART_COUPON_STORAGE_KEY, urlCode.trim());
      } catch (e) {
        /* ignore */
      }
    }
  }

  _getDisplayCode() {
    try {
      const stored = sessionStorage.getItem(CART_COUPON_STORAGE_KEY);
      if (stored) return stored;
    } catch (e) {
      /* ignore */
    }
    return this.dataset.fallbackCode || this._tagText?.textContent?.trim() || '';
  }

  _updateTagLabel() {
    if (!this._tagText || !this.classList.contains('cart-coupon--applied')) return;
    const label = this._getDisplayCode();
    if (label) this._tagText.textContent = label;
  }

  _syncAppliedUiFromDom() {
    const applied = this.classList.contains('cart-coupon--applied');
    if (this._input) {
      this._input.disabled = applied;
      if (!applied) this._input.value = '';
    }
    if (applied) {
      this._updateTagLabel();
    } else {
      const urlCode = new URLSearchParams(window.location.search).get('discount');
      let checkoutOnly = false;
      try {
        checkoutOnly = sessionStorage.getItem(CART_COUPON_CHECKOUT_ONLY_KEY) === '1';
      } catch (e) {
        /* ignore */
      }
      if (!urlCode && !checkoutOnly) {
        try {
          sessionStorage.removeItem(CART_COUPON_STORAGE_KEY);
        } catch (e) {
          /* ignore */
        }
      }
    }
  }

  _setApplied(applied) {
    this.classList.toggle('cart-coupon--applied', applied);
    this._ensureAppliedMarkup(applied);
    if (this._input) {
      this._input.disabled = applied;
      if (!applied) this._input.value = '';
    }
    if (applied) {
      this._updateTagLabel();
    }
    this._syncAppliedUiFromDom();
  }

  /** Coupon lives outside .js-contents so section refresh does not replace this element. */
  _ensureAppliedMarkup(applied) {
    const row = this.querySelector('.cart-coupon__row');
    if (!row) return;

    if (applied) {
      if (!this._tag) {
        const field = this.querySelector('.cart-coupon__field');
        if (!field) return;
        const tag = document.createElement('span');
        tag.className = 'cart-coupon__tag';
        const removeAria =
          window.cartStrings?.couponRemoveAria || 'Remove coupon';
        tag.innerHTML =
          '<span class="cart-coupon__tag-text"></span>' +
          `<button type="button" class="cart-coupon__tag-remove" aria-label="${removeAria}">` +
          '<span aria-hidden="true">&times;</span></button>';
        field.insertBefore(tag, field.firstChild);
      }
      this._applyBtn?.remove();
      if (!this._removeBtn) {
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'cart-coupon__btn cart-coupon__btn--remove';
        removeBtn.textContent =
          window.cartStrings?.couponRemove || 'REMOVE';
        row.appendChild(removeBtn);
      }
    } else {
      this._tag?.remove();
      this._removeBtn?.remove();
      if (!this._applyBtn) {
        const applyBtn = document.createElement('button');
        applyBtn.type = 'button';
        applyBtn.className = 'cart-coupon__btn cart-coupon__btn--apply';
        applyBtn.textContent =
          window.cartStrings?.couponApply || 'APPLY';
        row.appendChild(applyBtn);
      }
    }

    this._cacheElements();
    this._unbindEvents();
    this._bindEvents();
  }

  _syncVisibilityFromCart(itemCount) {
    if (typeof itemCount !== 'number') return;
    this.hidden = itemCount === 0;
  }

  _decodeHtmlEntities(text) {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  _showError(message, type = 'error') {
    if (!this._errorEl) return;
    let text = message;
    if (message && typeof message === 'object') {
      text =
        message.description || message.message || message.errors || '';
    }
    this._errorEl.textContent = this._decodeHtmlEntities(String(text || ''));
    this._errorEl.hidden = !text;
    this._errorEl.classList.toggle('cart-coupon__error--success', type === 'success');
  }

  _syncAppliedFromCartState(parsedState) {
    if (!parsedState) return;
    const apps = parsedState.cart_level_discount_applications;
    if (!Array.isArray(apps)) return;

    const discountCode = apps.find(
      (app) => this._getDiscountApplicationType(app) === 'discount_code'
    );
    if (discountCode) {
      const label = this._getDiscountApplicationTitle(discountCode);
      if (label) {
        try {
          sessionStorage.setItem(CART_COUPON_STORAGE_KEY, label);
        } catch (e) {
          /* ignore */
        }
      }
      this._setApplied(true);
      return;
    }

    if (!this.classList.contains('cart-coupon--checkout-only')) {
      this._setApplied(false);
    }
  }

  _showApplyFailure(message, code, parsedState) {
    this._syncAppliedFromCartState(parsedState);
    if (parsedState?.sections) {
      this._renderSections(parsedState);
    }
    this._showError(
      message || window.cartStrings?.couponErrorInvalid || 'Invalid coupon code'
    );
    if (this._input) this._input.value = code;
  }

  _getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  _restoreCheckoutOnlyState() {
    try {
      if (sessionStorage.getItem(CART_COUPON_CHECKOUT_ONLY_KEY) !== '1') return;
      const code = sessionStorage.getItem(CART_COUPON_STORAGE_KEY);
      if (!code) return;
      this.classList.add('cart-coupon--applied', 'cart-coupon--checkout-only');
      if (this._input) {
        this._input.value = code;
        this._input.disabled = true;
      }
      this._updateTagLabel();
      this._showError(window.cartStrings?.couponSuccessCheckout, 'success');
    } catch (e) {
      /* ignore */
    }
  }

  _setCheckoutOnlyApplied(code) {
    try {
      sessionStorage.setItem(CART_COUPON_STORAGE_KEY, code);
      sessionStorage.setItem(CART_COUPON_CHECKOUT_ONLY_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    this.classList.add('cart-coupon--applied', 'cart-coupon--checkout-only');
    if (this._input) {
      this._input.value = code;
      this._input.disabled = true;
    }
    this._updateTagLabel();
    this._showError(window.cartStrings?.couponSuccessCheckout, 'success');
  }

  _clearCheckoutOnlyState() {
    try {
      sessionStorage.removeItem(CART_COUPON_CHECKOUT_ONLY_KEY);
      sessionStorage.removeItem(CART_COUPON_STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
    this.classList.remove('cart-coupon--checkout-only');
  }

  _setLoading(loading) {
    this.classList.toggle('cart-coupon--loading', loading);
    if (this._applyBtn) this._applyBtn.disabled = loading;
    if (this._removeBtn) this._removeBtn.disabled = loading;
    this.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  _getSectionsToRender() {
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    if (this.closest('cart-drawer')) {
      sections.unshift(
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.js-contents',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__header',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.cart-drawer__footer',
        }
      );
      return sections;
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (!mainCartItems?.dataset?.id) return [];

    sections.unshift(
      {
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    );

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter?.dataset?.id) {
      sections.push(
        {
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '.js-contents-footer',
        },
        {
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '.js-contents-shiping',
        }
      );
    }

    return sections;
  }

  _getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  _renderSections(parsedState) {
    if (!parsedState?.sections) return;

    this._getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.section];
      if (!sectionHtml) return;

      const container = document.getElementById(section.id);
      if (!container) return;

      const inner = this._getSectionInnerHTML(sectionHtml, section.selector);
      if (inner == null) return;

      const target =
        container.querySelector(section.selector) || container;
      target.innerHTML = inner;
    });

    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof parsedState.item_count === 'number') {
      cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && typeof parsedState.item_count === 'number') {
      cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (cartDrawerWrapper && typeof parsedState.item_count === 'number') {
      cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof parsedState.item_count === 'number') {
      cartDrawerItems.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this._syncVisibilityFromCart(parsedState.item_count);
  }

  _normalizeCode(code) {
    return (code || '').trim().toUpperCase();
  }

  /** Cart Ajax API nests fields under discount_application; Liquid uses flat objects. */
  _getDiscountApplication(entry) {
    if (!entry || typeof entry !== 'object') return null;
    if (
      entry.discount_application &&
      typeof entry.discount_application === 'object'
    ) {
      return entry.discount_application;
    }
    return entry;
  }

  _getDiscountApplicationType(entry) {
    return this._getDiscountApplication(entry)?.type || '';
  }

  _getDiscountApplicationTitle(entry) {
    return this._getDiscountApplication(entry)?.title || '';
  }

  _hasCartLevelDiscount(cartState) {
    const apps = cartState?.cart_level_discount_applications;
    return (
      Array.isArray(apps) &&
      apps.some(
        (app) => this._getDiscountApplicationType(app) === 'discount_code'
      )
    );
  }

  _hasDiscountCodeOnCart(cartState, code) {
    const normalized = this._normalizeCode(code);
    if (!normalized) return false;

    const discountCodes = cartState?.discount_codes;
    if (Array.isArray(discountCodes)) {
      return discountCodes.some((entry) => {
        const entryCode =
          typeof entry === 'string' ? entry : entry?.code || entry?.title || '';
        return this._normalizeCode(entryCode) === normalized;
      });
    }

    const apps = cartState?.cart_level_discount_applications;
    if (Array.isArray(apps)) {
      return apps.some((app) => {
        if (this._getDiscountApplicationType(app) !== 'discount_code') {
          return false;
        }
        return this._normalizeCode(this._getDiscountApplicationTitle(app)) === normalized;
      });
    }

    return false;
  }

  _hasDiscountApplied(cartState, code) {
    if (!code) return !this._hasCartLevelDiscount(cartState);
    return this._hasDiscountCodeOnCart(cartState, code);
  }

  _getDiscountUpdateError(cartState, code) {
    const discountCodes = cartState?.discount_codes;
    if (!Array.isArray(discountCodes)) return null;

    const normalized = this._normalizeCode(code);
    for (const entry of discountCodes) {
      const entryCode =
        typeof entry === 'string' ? entry : entry?.code || entry?.title || '';
      if (this._normalizeCode(entryCode) !== normalized) continue;
      if (entry.applicable === false) {
        return (
          entry.error ||
          entry.notice ||
          window.cartStrings?.couponErrorInvalid
        );
      }
    }
    return null;
  }

  async _fetchCartJs() {
    const root = this._getShopifyRoot();
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return response.json();
  }

  async _requestCartUpdate({ discount, sections }) {
    const sectionConfigs = sections || this._getSectionsToRender();
    const payload = {
      sections: sectionConfigs.map((s) => s.section),
      sections_url: window.location.pathname,
    };
    if (typeof discount === 'string') payload.discount = discount;

    const fetchOpts = fetchConfig('json');
    fetchOpts.credentials = 'same-origin';
    fetchOpts.headers = {
      ...fetchOpts.headers,
      'X-Requested-With': 'XMLHttpRequest',
    };
    fetchOpts.body = JSON.stringify(payload);

    const response = await fetch(getCartUpdateJsUrl(), fetchOpts);
    const text = await response.text();

    if (text.trim().startsWith('<') || text.trim().startsWith('<!DOCTYPE')) {
      throw new Error('HTML_RESPONSE');
    }

    const parsedState = JSON.parse(text);
    if (
      !response.ok ||
      (parsedState.status && parsedState.status >= 400)
    ) {
      const err = new Error('CART_UPDATE_FAILED');
      err.parsedState = parsedState;
      throw err;
    }
    return parsedState;
  }

  async _applyViaDiscountRoute(code) {
    const root = this._getShopifyRoot();
    const redirect = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    await fetch(
      `${root}discount/${encodeURIComponent(code)}?redirect=${redirect}`,
      { credentials: 'same-origin', redirect: 'follow' }
    );
  }

  async _removeViaDiscountRoute() {
    const root = this._getShopifyRoot();
    const redirect = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    await fetch(`${root}discount/none?redirect=${redirect}`, {
      credentials: 'same-origin',
      redirect: 'follow',
    });
  }

  _finishApplySuccess(parsedState, code, checkoutOnly) {
    try {
      sessionStorage.setItem(CART_COUPON_STORAGE_KEY, code);
      if (checkoutOnly) {
        sessionStorage.setItem(CART_COUPON_CHECKOUT_ONLY_KEY, '1');
      } else {
        sessionStorage.removeItem(CART_COUPON_CHECKOUT_ONLY_KEY);
      }
    } catch (e) {
      /* ignore */
    }

    this._setApplied(true);
    if (parsedState?.sections) this._renderSections(parsedState);
  }

  async _updateDiscount(discountValue, submittedCode) {
    const sections = this._getSectionsToRender();
    if (!sections.length) return;

    this._setLoading(true);
    this._showError('');
    this.classList.remove('cart-coupon--checkout-only');

    try {
      if (!discountValue) {
        this._clearCheckoutOnlyState();
        let parsedState = await this._requestCartUpdate({ discount: '', sections });
        if (!this._hasCartLevelDiscount(parsedState)) {
          await this._removeViaDiscountRoute();
          parsedState = await this._requestCartUpdate({ sections });
        }
        this._setApplied(false);
        this._renderSections(parsedState);
        return;
      }

      const code = submittedCode || discountValue;
      let parsedState = await this._requestCartUpdate({ discount: code, sections });

      if (parsedState.errors) {
        this._showApplyFailure(parsedState.errors, code, parsedState);
        return;
      }

      const discountError = this._getDiscountUpdateError(parsedState, code);
      if (discountError) {
        this._showApplyFailure(discountError, code, parsedState);
        return;
      }

      if (this._hasDiscountApplied(parsedState, code)) {
        this._finishApplySuccess(parsedState, code, false);
        return;
      }

      this._showApplyFailure(
        window.cartStrings?.couponErrorInvalid,
        code,
        parsedState
      );
      return;
    } catch (e) {
      if (e.message === 'HTML_RESPONSE') {
        this._showError(window.cartStrings?.error || 'Error');
        return;
      }
      if (e.message === 'CART_UPDATE_FAILED') {
        this._showApplyFailure(
          e.parsedState?.description ||
            e.parsedState?.message ||
            window.cartStrings?.couponErrorInvalid,
          submittedCode || '',
          e.parsedState
        );
        return;
      }
      console.error(e);
      this._showError(window.cartStrings?.error || 'Error');
    } finally {
      this._setLoading(false);
    }
  }

  _onApply() {
    const code = (this._input?.value || '').trim();
    if (!code) {
      this._showError(window.cartStrings?.couponErrorEmpty);
      this._input?.focus();
      return;
    }
    this._updateDiscount(code, code);
  }

  _onRemove() {
    this._updateDiscount('', null);
  }
}

if (!customElements.get('cart-coupon')) {
  customElements.define('cart-coupon', CartCoupon);
}

initCartDrawerCouponPositionObserver();
